import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import {
  AsyncValidatorFn,
  FormControl,
  ReactiveFormsModule,
  ValidatorFn,
} from '@angular/forms';
import { IconComponent } from '../icons/icon-reusable';
import { emailIcon, eyeCloseIcon, eyeOpenIcon, IconDef } from '../icons/icon';
import { TextCaseMode, TextCaseReusableDirective } from '../text-case-reusable/text-case-reusable';
import { CustomValidators } from '../../utils/custom-validators';
 
type InputReusableType =
  | 'text'
  | 'email'
  | 'password'
  | 'number'
  | 'search'
  | 'tel'
  | 'url'
  | 'date'
  | 'time'
  | 'textarea';

type InputReusableSize = 'sm' | 'md' | 'lg';
 
@Component({
  selector: 'app-input-reusable',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, IconComponent, TextCaseReusableDirective],
  templateUrl: './input-reusable.html',
  styles: [
    `
      input[type='password']::-ms-reveal,
      input[type='password']::-ms-clear {
        display: none;
      }
    `,
  ],
})
export class InputReusable implements OnInit, OnChanges, OnDestroy {
  ngOnDestroy(): void {
    if (this.disabledByInput && this.control.disabled) {
      this.control.enable({ emitEvent: false });
    }
  }

  private static nextUniqueId = 0;

  @Input() type: InputReusableType = 'text';
  @Input() size: InputReusableSize = 'lg';
  @Input() label = '';
  @Input() labelClass = 'mb-1 block text-sm font-medium !text-slate900';
  @Input() placeholder = '';
  @Input() name = '';
  @Input() id = '';
  @Input() autocomplete = '';
  @Input() control: FormControl<string | number | null> = new FormControl<string | number | null>('');
  @Input() inputId = '';
  @Input() testId = '';
  @Input() required = false;
  @Input() readonly = false;
  @Input() disabled = false;
  @Input() textCase: TextCaseMode = 'none';
  @Input() preventPaste = false;
  @Input() preventCopy = false;
  @Input() suffixIcon: IconDef | null = null;
  @Input() externalErrorMessage = '';
  @Input() min: number | null = null;
  @Input() max: number | null = null;
  @Input() step: number | null = null;
  @Input() pattern = '';
  @Input() enforcePasswordStrength = false;
 
  showPassword = false;
  readonly emailIcon = emailIcon;
  readonly eyeOpenIcon = eyeOpenIcon;
  readonly eyeCloseIcon = eyeCloseIcon;
  readonly generatedId = `input-reusable-${InputReusable.nextUniqueId++}`;
  private baseValidator: ValidatorFn | null = null;
  private baseAsyncValidator: AsyncValidatorFn | null = null;
  private disabledByInput = false;
 
  ngOnInit(): void {
    this.captureBaseValidators();
    this.syncDisabledState();
    this.applyInputSpecificValidation();
  }
 
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['control']) {
      this.captureBaseValidators();
      this.applyInputSpecificValidation();
      this.syncDisabledState();
    }
 
    if (changes['disabled']) {
      this.syncDisabledState();
    }
 
    if (changes['type'] || changes['required'] || changes['enforcePasswordStrength']) {
      this.applyInputSpecificValidation();
      this.showPassword = false;
    }
  }
 
  get resolvedType(): string {
    if (this.type === 'textarea') {
      return 'text';
    }
 
    if (this.type === 'password') {
      return this.showPassword ? 'text' : 'password';
    }
 
    return this.type;
  }
 
  get isTextArea(): boolean {
    return this.type === 'textarea';
  }
 
  get canClear(): boolean {
    const currentValue = this.control.value;
    return (
      (this.type === 'text' || this.type === 'search') &&
      !this.isDisabled &&
      !this.readonly &&
      currentValue !== null &&
      `${currentValue}`.length > 0
    );
  }

  get hasRightAdornment(): boolean {
    return this.canClear || this.type === 'password' || this.type === 'email' || !!this.suffixIcon;
  }

  get inputClassMap(): Record<string, boolean> {
    return {
      'h-[46px] py-2.5 placeholder:text-[14px]': this.size === 'lg',
      'h-10 py-2 placeholder:text-[14px]': this.size === 'md',
      'h-9 py-1.5 placeholder:text-xs': this.size === 'sm',
      '!text-sm text-slate900': this.size === 'lg',
      '!text-[14px] text-slate900': this.size === 'md',
      '!text-xs text-slate900': this.size === 'sm',
      'pl-3': true,
      'pr-3': !this.hasRightAdornment,
      'pr-8': this.hasRightAdornment && this.size === 'sm',
      'pr-9': this.hasRightAdornment && this.size === 'md',
      'pr-10': this.hasRightAdornment && this.size === 'lg',
    };
  }

  get textareaClassMap(): Record<string, boolean> {
    return {
      '!px-3 py-2.5': this.size === 'lg',
      '!px-3 py-2': this.size === 'md',
      '!px-3 py-1.5': this.size === 'sm',
      '!text-sm text-slate900': this.size === 'lg',
      '!text-[14px] text-slate900': this.size === 'md',
      '!text-xs text-slate900': this.size === 'sm',
    };
  }

  get adornmentIconSize(): number {
    return this.size === 'md' ? 20 : 20;
  }
 
  get shouldShowError(): boolean {
    return this.control.invalid && (this.control.touched || this.control.dirty);
  }
 
  get shouldShowAnyError(): boolean {
    return !!this.externalErrorMessage || this.shouldShowError;
  }
 
  get resolvedErrorMessage(): string {
    return this.externalErrorMessage || this.firstErrorMessage;
  }
 
  get isSuccessState(): boolean {
    return this.control.valid && (this.control.touched || this.control.dirty);
  }
 
  get isDisabled(): boolean {
    return this.disabled || this.control.disabled;
  }
 
  get firstErrorMessage(): string {
    const errors = this.control.errors;
    if (!errors) {
      return '';
    }
 
    if (errors['required']) {
      return `${this.fieldName} is required.`;
    }
 
    if (errors['email']) {
      return 'Please enter a valid email address.';
    }

    if (errors['emailsSame']) {
      return 'Company email and Compliance email must not be the same.';
    }
 
    if (errors['url']) {
      return `${this.fieldName} must be a valid URL (e.g. https://example.com).`;
    }
 
    if (errors['companyNumber']) {
      return `${this.fieldName} must be 8 digits or 2 letters followed by 6 digits.`;
    }

    if (errors['fcaFirmReferenceNumber']) {
      return `${this.fieldName} must be 6 to 10 digits.`;
    }

    if (errors['address']) {
      return `${this.fieldName} is required and cannot exceed 255 characters.`;
    }

    if (errors['city']) {
      return `${this.fieldName} must contain only letters (max 100 characters, no spaces).`;
    }

    if (errors['companyNameOrTitle']) {
      return `${this.fieldName} is required and cannot exceed 100 characters.`;
    }

    if (errors['password']) {
      return 'Password must be 8+ characters with uppercase, lowercase, number, and symbol.';
    }

    if (errors['passwordMismatch']) {
      return 'Passwords do not match.';
    }

    if (errors['mismatch']) {
      return 'Values do not match.';
    }
 
    if (errors['postcode']) {
      return 'Please enter a valid UK postcode (e.g. SW1A 1AA).';
    }
 
    if (errors['phone']) {
      return `${this.fieldName} must be a valid phone number (10-15 digits).`;
    }
 
    if (errors['telephone']) {
      return `${this.fieldName} must be a valid telephone number (8-15 digits).`;
    }
 
    if (errors['niNumber']) {
      return 'Please enter a valid NI number (e.g. AB123456C).';
    }
 
    if (errors['range']) {
      return `${this.fieldName} must be between ${errors['range'].min} and ${errors['range'].max}.`;
    }
 
    if (errors['hasNumbers']) {
      return `${this.fieldName} cannot contain numbers.`;
    }
 
    if (errors['lettersOnly']) {
      return `${this.fieldName} can include letters, spaces, and special characters only (no numbers, max 50).`;
    }
   if (errors['fullName']) {
      return `${this.fieldName} can include letters, spaces, and special characters only (no numbers, max 100).`;
    }
    if (errors['minAge']) {
      return `${this.fieldName} must be at least ${errors['minAge'].required} years old.`;
    }
 
    if (errors['futureDate'] || errors['notFutureDate']) {
      return `${this.fieldName} cannot be a future date.`;
    }

    if (errors['pastDate'] || errors['notPastDate']) {
      return `${this.fieldName} cannot be a past date.`;
    }
 
    if (errors['requiredBoolean']) {
      return `${this.fieldName} is required.`;
    }
 
    if (errors['minlength']) {
      return `Minimum ${errors['minlength'].requiredLength} characters required.`;
    }
 
    if (errors['maxlength']) {
      return `Maximum ${errors['maxlength'].requiredLength} characters allowed.`;
    }
 
    if (errors['pattern']) {
      return 'Please enter a valid value.';
    }

    if (errors['fileType'] || errors['documentFileType']) {
      return 'Accepted formats: PDF, PNG, JPG, JPEG.';
    }

    if (errors['fileSize']) {
      return `File size must be ${errors['fileSize'].maxSizeMB}MB or less.`;
    }

    if (errors['dobFormat']) {
      return 'Date of birth must be in dd-mm-yyyy format.';
    }

    if (errors['dobPastDate']) {
      return 'Date of birth must be a valid past date.';
    }

    if (errors['dobMinAge']) {
      return `Minimum age is ${errors['dobMinAge'].required} years.`;
    }
 
    return 'Invalid input.';
  }
 
  get fieldName(): string {
    return this.label || 'This field';
  }

  get resolvedId(): string {
    return this.id || this.generatedId;
  }
 
  clear(): void {
    if (this.isDisabled || this.readonly) {
      return;
    }
 
    this.control.setValue('');
    this.control.markAsDirty();
    this.control.markAsTouched();
    this.control.updateValueAndValidity();
  }
 
  togglePasswordVisibility(): void {
    if (this.type !== 'password') {
      return;
    }
 
    this.showPassword = !this.showPassword;
  }
 
  handleInput(event: Event): void {
    const inputElement = event.target as HTMLInputElement;

    if (this.type !== 'number') {
      const transformed = this.applyTextCaseToValue(inputElement.value);
      if (transformed !== inputElement.value) {
        inputElement.value = transformed;
        this.control.setValue(transformed);
      }
      return;
    }
 
    let sanitized = inputElement.value.replace(/[^0-9.-]/g, '');

    if (this.min !== null && this.min >= 0) {
      sanitized = sanitized.replace(/-/g, '');
    }
 
    if (sanitized !== inputElement.value) {
      inputElement.value = sanitized;
      this.control.setValue(sanitized);
    }
  }

  handleFocus(event: Event): void {
    if (this.type !== 'number') return;
    const input = event.target as HTMLInputElement;
    const val = String(this.control.value ?? '').trim();
    if (val === '0' || val === '0.00' || val === '0.0') {
      this.control.setValue('');
      input.value = '';
    }
  }

  handleBlur(event: Event): void {
    if (this.type !== 'number') return;
    const input = event.target as HTMLInputElement;
    const val = String(this.control.value ?? '').trim();
    if (val === '') {
      // leave empty (we treat empty as undefined on serializer)
      this.control.setValue('');
      input.value = '';
      return;
    }
    // sanitize to numeric and remove leading zeros
    const n = parseFloat(val.replace(/,/g, ''));
    if (isNaN(n)) {
      this.control.setValue('');
      input.value = '';
    } else {
      const normalized = String(n);
      this.control.setValue(normalized);
      input.value = normalized;
    }
  }

  private applyTextCaseToValue(value: string): string {
    if (this.textCase === 'uppercase') {
      return value.toUpperCase();
    }

    if (this.textCase === 'lowercase') {
      return value.toLowerCase();
    }

    if (this.textCase === 'titlecase') {
      return value
        .toLowerCase()
        .split(/(\s+)/)
        .map((part) => (/^\s+$/.test(part) ? part : part.charAt(0).toUpperCase() + part.slice(1)))
        .join('');
    }

    return value;
  }
 
  handlePaste(event: ClipboardEvent): void {
    if (!this.preventPaste) {
      return;
    }
 
    event.preventDefault();
  }

  handleCopy(event: ClipboardEvent): void {
    if (!this.preventCopy) {
      return;
    }

    event.preventDefault();
  }
 
  private syncDisabledState(): void {
    if (this.disabled && this.control.enabled) {
      this.control.disable({ emitEvent: false });
      this.disabledByInput = true;
    }
 
    if (!this.disabled && this.disabledByInput && this.control.disabled) {
      this.control.enable({ emitEvent: false });
      this.disabledByInput = false;
    }
  }
 
  private applyInputSpecificValidation(): void {
    const validators = this.baseValidator ? [this.baseValidator] : [];
 
    if (this.required) {
      validators.push(CustomValidators.required);
    }
 
    if (this.type === 'email') {
      validators.push(CustomValidators.email);
    }

    if (this.type === 'url') {
      validators.push(CustomValidators.url);
    }

    if (this.type === 'tel') {
      validators.push(CustomValidators.phone);
    }

    if (this.type === 'password' && this.enforcePasswordStrength) {
      validators.push(CustomValidators.password);
    }
 
    this.control.setValidators(validators);
    this.control.setAsyncValidators(this.baseAsyncValidator);
    this.control.updateValueAndValidity({ emitEvent: false });
  }
 
  private captureBaseValidators(): void {
    this.baseValidator = this.control.validator;
    this.baseAsyncValidator = this.control.asyncValidator;
  }
 
}
